// Genel: marka, şube, açıklama ve görseller.
import React from 'react';
import { Autocomplete, Box, Button, Paper, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import { useTranslation } from 'react-i18next';
import type { StockCardForm } from '../useStockCardForm';
import { GroupLabel, SectionTitle } from './fields';

export const GeneralSection: React.FC<{ f: StockCardForm }> = ({ f }) => {
  const { t } = useTranslation('accounting');

  return (
    <Box>
      <SectionTitle
        title={t('stock.addModal.layout.sections.general', 'Genel')}
        hint={t('stock.addModal.layout.generalHint', 'Ad, kod, birim ve kategori üstte. Burada marka, şube, açıklama ve görseller var.')}
      />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start', minWidth: 0 }}>
          <Autocomplete
            fullWidth
            size="small"
            id="stock-card-brand"
            options={f.brands}
            getOptionLabel={(option) => option.name}
            value={f.selectedBrand}
            onChange={(_, value) => f.handleBrandChange(value)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} label={t('stock.addModal.fields.brand')} size="small" />}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <BrandingWatermarkIcon fontSize="small" sx={{ mr: 1, color: 'action.active' }} />
                <Typography variant="body2">{option.name}</Typography>
              </li>
            )}
          />
          <Tooltip title={t('stock.addModal.layout.newBrand', 'Yeni marka')}>
            <Button
              variant="outlined"
              size="small"
              onClick={f.handleOpenBrandModal}
              aria-label={t('stock.addModal.layout.newBrand', 'Yeni marka')}
              sx={{ minWidth: 40, height: 40, p: 0 }}
            >
              <AddIcon fontSize="small" />
            </Button>
          </Tooltip>
        </Box>

        <Autocomplete
          fullWidth
          size="small"
          id="stock-card-branch"
          options={f.branches}
          getOptionLabel={(option) => option.name}
          value={f.selectedBranch}
          onChange={(_, value) => f.handleBranchChange(value)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('stock.addModal.layout.branch', 'Şube')}
              placeholder={t('stock.addModal.layout.allBranches', 'Tüm şubeler')}
              helperText={t('stock.addModal.layout.branchHint', 'Boş bırakılırsa ürün tüm şubelerde görünür')}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1.15rem' }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>

      <TextField
        id="stock-card-description"
        fullWidth
        size="small"
        multiline
        minRows={3}
        sx={{ mt: 1.5 }}
        label={t('stock.addModal.fields.description')}
        helperText={t('stock.addModal.layout.descriptionHint', 'Satış ekranında, QR menüde ve e-ticarette görünebilir')}
        value={f.description}
        onChange={(e) => f.setDescription(e.target.value)}
      />

      <GroupLabel>{t('stock.addModal.sections.imageUpload')}</GroupLabel>
      <Paper variant="outlined" sx={{ p: 1.25, bgcolor: 'background.default' }}>
        {f.imageUrls.length === 0 ? (
          <Typography variant="body2" color="text.secondary">{t('stock.addModal.image.noImageSelected')}</Typography>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 1 }}>
            {f.imageUrls.map((url, index) => (
              <Box key={`${url}-${index}`} sx={{ position: 'relative', borderRadius: 1, overflow: 'hidden', border: '1px solid', borderColor: index === 0 ? 'primary.main' : 'divider' }}>
                <Box component="img" src={url} alt="" sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 0.5, bgcolor: 'background.paper' }}>
                  <Typography variant="caption" color={index === 0 ? 'primary' : 'text.secondary'}>
                    {index === 0 ? t('stock.addModal.image.coverImage') : `${index + 1}.`}
                  </Typography>
                  <Button size="small" color="error" onClick={() => f.handleRemoveImageUrl(url)} sx={{ minWidth: 0, px: 0.5 }}>
                    {t('stock.addModal.actions.remove')}
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}
        <Button variant="outlined" size="small" sx={{ mt: 1 }} onClick={() => f.setIsImageLibraryModalOpen(true)}>
          {t('stock.addModal.actions.addImage')}
        </Button>
      </Paper>
    </Box>
  );
};
